import { supabase } from './supabase';

interface EmailTemplate {
  id: string;
  template_key: string;
  subject: string;
  html_content: string;
  text_content: string;
  variables: string[];
  is_active: boolean;
}

interface EmailData {
  [key: string]: any;
}

class EmailService {
  private async getTemplate(templateKey: string): Promise<EmailTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_key', templateKey)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching email template:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching email template:', error);
      return null;
    }
  }

  private replaceVariables(content: string, data: EmailData): string {
    let result = content;
    
    // Replace simple variables like {{variable_name}}
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, String(data[key] || ''));
    });

    // Handle conditional blocks with parent context like {{#if ../variable}}...{{/if}}
    result = result.replace(/{{#if\s+\.\.\/(\w+)}}(.*?){{\/if}}/gs, (match, variable, content) => {
      return data[variable] ? content : '';
    });

    // Handle conditional blocks like {{#if variable}}...{{/if}}
    result = result.replace(/{{#if\s+(\w+)}}(.*?){{\/if}}/gs, (match, variable, content) => {
      return data[variable] ? content : '';
    });

    // Handle conditional blocks with comparison like {{#if variable > value}}...{{/if}}
    result = result.replace(/{{#if\s+(\w+)\s*>\s*(\w+)}}(.*?){{\/if}}/gs, (match, variable, value, content) => {
      return data[variable] > value ? content : '';
    });

    // Handle conditional blocks with not equal comparison like {{#if variable != value}}...{{/if}}
    result = result.replace(/{{#if\s+(\w+)\s*!=\s*(\w+)}}(.*?){{\/if}}/gs, (match, variable, value, content) => {
      return data[variable] != value ? content : '';
    });

    // Handle categories loop - replace [categories_list] with formatted categories
    if (data.categories && Array.isArray(data.categories)) {
      const categoriesText = data.categories.map(category => {
        let categoryText = `- ${category.name} (${category.vehicle_type})`;
        categoryText += `\n  Registration Fee: xcg ${category.registration_fee}`;
        if (data.tickets_enabled) {
          if (category.wristbands_included > 0) {
            categoryText += `\n  Includes: ${category.wristbands_included} wristbands`;
          }
          if (category.tickets_included > 0) {
            categoryText += `${category.wristbands_included > 0 ? ', ' : '\n  Includes: '}${category.tickets_included} tickets`;
          }
        }
        if (category.first_place_prize > 0) {
          categoryText += `\n  1st Place Prize: xcg ${category.first_place_prize}`;
        }
        if (category.second_place_prize > 0) {
          categoryText += `\n  2nd Place Prize: xcg ${category.second_place_prize}`;
        }
        if (category.time_range) {
          categoryText += `\n  Time Range: ${category.time_range}`;
        }
        return categoryText;
      }).join('\n\n');
      
      result = result.replace(/\[categories_list\]/g, categoriesText);
    }

    // Handle loops like {{#each array}}...{{/each}}
    result = result.replace(/{{#each\s+(\w+)}}(.*?){{\/each}}/gs, (match, arrayName, template) => {
      const array = data[arrayName];
      if (!Array.isArray(array)) return '';
      
      return array.map(item => {
        let itemContent = template;
        
        // Handle parent context references like {{../variable}}
        itemContent = itemContent.replace(/{{\.\.\/(\w+)}}/g, (match, parentVar) => {
          return String(data[parentVar] || '');
        });
        
        // Handle item properties
        Object.keys(item).forEach(key => {
          const regex = new RegExp(`{{${key}}}`, 'g');
          itemContent = itemContent.replace(regex, String(item[key] || ''));
        });
        
        return itemContent;
      }).join('');
    });

    return result;
  }

  private async logEmail(
    userId: string | null,
    templateKey: string,
    recipientEmail: string,
    subject: string,
    status: 'pending' | 'sent' | 'failed',
    errorMessage?: string
  ) {
    try {
      await supabase
        .from('email_logs')
        .insert([
          {
            user_id: userId,
            template_key: templateKey,
            recipient_email: recipientEmail,
            subject,
            status,
            error_message: errorMessage,
            sent_at: status === 'sent' ? new Date().toISOString() : null
          }
        ]);
    } catch (error) {
      console.error('Error logging email:', error);
    }
  }

  private async sendEmailViaEdgeFunction(
    to: string,
    subject: string,
    html: string,
    text: string
  ): Promise<{ success: boolean; error?: string; details?: string }> {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject,
          html,
          text
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      return { success: true };
    } catch (error) {
      console.error('Error calling send-email edge function:', error);
      
      let errorMessage = error instanceof Error ? error.message : 'Unknown error';
      let details = 'Please try again later';
      
      // Handle specific SMTP configuration errors
      if (errorMessage.includes('SMTP credentials not properly configured') || 
          errorMessage.includes('SMTP credentials not configured in vault or environment variables')) {
        details = 'Email service is not configured. Please contact administrator to set up SMTP credentials.';
      }
      
      return { 
        success: false, 
        error: errorMessage,
        details: details
      };
    }
  }

  async sendTrackBookingConfirmation(
    userId: string,
    userEmail: string,
    bookingData: {
      customer_name: string;
      track_type: string;
      booking_date: string;
      booking_time: string;
      duration: number;
      booking_type: string;
      traction_compound?: boolean;
      special_requests?: string;
      total_amount: number;
      deposit_amount?: number;
    }
  ): Promise<boolean> {
    try {
      const template = await this.getTemplate('track_booking_confirmation');
      if (!template) {
        console.error('Track booking confirmation template not found');
        await this.logEmail(userId, 'track_booking_confirmation', userEmail, 'Track Booking Confirmation', 'failed', 'Template not found');
        return false;
      }

      const subject = this.replaceVariables(template.subject, bookingData);
      const htmlContent = this.replaceVariables(template.html_content, bookingData);
      const textContent = this.replaceVariables(template.text_content, bookingData);

      // Log the email as pending
      await this.logEmail(userId, 'track_booking_confirmation', userEmail, subject, 'pending');

      // Send email via Supabase Edge Function
      const result = await this.sendEmailViaEdgeFunction(userEmail, subject, htmlContent, textContent);

      if (result.success) {
        // Log as sent
        await this.logEmail(userId, 'track_booking_confirmation', userEmail, subject, 'sent');
        return true;
      } else {
        // Log as failed with detailed error
        const errorDetails = result.details ? `${result.error} - ${result.details}` : result.error;
        await this.logEmail(userId, 'track_booking_confirmation', userEmail, subject, 'failed', errorDetails);
        return false;
      }
    } catch (error) {
      console.error('Error sending track booking confirmation:', error);
      await this.logEmail(userId, 'track_booking_confirmation', userEmail, 'Track Booking Confirmation', 'failed', String(error));
      return false;
    }
  }

  async sendPromotorBookingNotification(
    userId: string,
    userEmail: string,
    bookingData: {
      customer_name: string;
      company_name: string;
      crib_number: string;
      kvk_number: string;
      phone: string;
      track_type: string;
      booking_date: string;
      booking_time: string;
      duration: number;
      booking_type: string;
      traction_compound?: boolean;
      special_requests?: string;
      total_amount: number;
      deposit_amount?: number;
    }
  ): Promise<boolean> {
    try {
      const template = await this.getTemplate('promotor_booking_notification');
      if (!template) {
        console.error('Promotor booking notification template not found');
        await this.logEmail(userId, 'promotor_booking_notification', userEmail, 'Promotor Booking Notification', 'failed', 'Template not found');
        return false;
      }

      const subject = this.replaceVariables(template.subject, bookingData);
      const htmlContent = this.replaceVariables(template.html_content, bookingData);
      const textContent = this.replaceVariables(template.text_content, bookingData);

      // Log the email as pending
      await this.logEmail(userId, 'promotor_booking_notification', userEmail, subject, 'pending');

      // Send email via Supabase Edge Function
      const result = await this.sendEmailViaEdgeFunction(userEmail, subject, htmlContent, textContent);

      if (result.success) {
        // Log as sent
        await this.logEmail(userId, 'promotor_booking_notification', userEmail, subject, 'sent');
        return true;
      } else {
        // Log as failed with detailed error
        const errorDetails = result.details ? `${result.error} - ${result.details}` : result.error;
        await this.logEmail(userId, 'promotor_booking_notification', userEmail, subject, 'failed', errorDetails);
        return false;
      }
    } catch (error) {
      console.error('Error sending promotor booking notification:', error);
      await this.logEmail(userId, 'promotor_booking_notification', userEmail, 'Promotor Booking Notification', 'failed', String(error));
      return false;
    }
  }

  async sendAdminPromotorBookingNotification(
    userId: string,
    promotorData: {
      customer_name: string;
      company_name: string;
      crib_number: string;
      kvk_number: string;
      phone: string;
      email: string;
      track_type: string;
      booking_date: string;
      booking_time: string;
      duration: number;
      booking_type: string;
      traction_compound?: boolean;
      special_requests?: string;
      total_amount: number;
      deposit_amount?: number;
    }
  ): Promise<boolean> {
    try {
      // const adminEmail = 'fac.autosportcuracao@outlook.com';
      const template = await this.getTemplate('admin_promotor_booking_notification');
      
      if (!template) {
        console.error('Admin promotor booking notification template not found');
        await this.logEmail(userId, 'admin_promotor_booking_notification', adminEmail, 'New Promotor Booking', 'failed', 'Template not found');
        return false;
      }

      const subject = this.replaceVariables(template.subject, promotorData);
      const htmlContent = this.replaceVariables(template.html_content, promotorData);
      const textContent = this.replaceVariables(template.text_content, promotorData);

      // Log the email as pending
      await this.logEmail(userId, 'admin_promotor_booking_notification', adminEmail, subject, 'pending');

      // Send email via Supabase Edge Function
      const result = await this.sendEmailViaEdgeFunction(adminEmail, subject, htmlContent, textContent);

      if (result.success) {
        // Log as sent
        await this.logEmail(userId, 'admin_promotor_booking_notification', adminEmail, subject, 'sent');
        return true;
      } else {
        // Log as failed with detailed error
        const errorDetails = result.details ? `${result.error} - ${result.details}` : result.error;
        await this.logEmail(userId, 'admin_promotor_booking_notification', adminEmail, subject, 'failed', errorDetails);
        return false;
      }
    } catch (error) {
      console.error('Error sending admin promotor booking notification:', error);
      await this.logEmail(userId, 'admin_promotor_booking_notification', 'fac.autosportcuracao@outlook.com', 'New Promotor Booking', 'failed', String(error));
      return false;
    }
  }

  async sendEventRegistrationConfirmation(
    userId: string,
    userEmail: string,
    registrationData: {
      driver_name: string;
      event_name: string;
      event_date: string;
      registration_deadline: string;
      categories: Array<{
        name: string;
        vehicle_type: string;
        registration_fee: number;
        wristbands_included: number;
        tickets_included: number;
        first_place_prize?: number;
        second_place_prize?: number;
        time_range?: string;
      }>;
      total_amount: number;
      has_tool_car?: boolean;
      tool_car_count?: number;
      tickets_enabled?: boolean;
      event_id?: string;
    }
  ): Promise<{ success: boolean; error?: string; details?: string }> {
    try {
      const template = await this.getTemplate('event_registration_confirmation');
      if (!template) {
        console.error('Event registration confirmation template not found');
        await this.logEmail(userId, 'event_registration_confirmation', userEmail, 'Event Registration Confirmation', 'failed', 'Template not found');
        return { success: false, error: 'Email template not found' };
      }

      // Log the template data for debugging
      console.log('Template found:', template);
      console.log('Registration data:', registrationData);

      const subject = this.replaceVariables(template.subject, registrationData);
      const htmlContent = this.replaceVariables(template.html_content, registrationData);
      const textContent = this.replaceVariables(template.text_content, registrationData);

      // Log the processed content for debugging
      console.log('Processed subject:', subject);
      console.log('Processed text content sample:', textContent.substring(0, 200) + '...');

      // Log the email as pending
      await this.logEmail(userId, 'event_registration_confirmation', userEmail, subject, 'pending');

      // Send email via Supabase Edge Function
      const result = await this.sendEmailViaEdgeFunction(userEmail, subject, htmlContent, textContent);

      if (result.success) {
        // Log as sent
        await this.logEmail(userId, 'event_registration_confirmation', userEmail, subject, 'sent');
        return { success: true };
      } else {
        // Log as failed with detailed error
        const errorDetails = result.details ? `${result.error} - ${result.details}` : result.error;
        await this.logEmail(userId, 'event_registration_confirmation', userEmail, subject, 'failed', errorDetails);
        return { 
          success: false, 
          error: result.error || 'Failed to send email',
          details: result.details
        };
      }
    } catch (error) {
      console.error('Error sending event registration confirmation:', error);
      await this.logEmail(userId, 'event_registration_confirmation', userEmail, 'Event Registration Confirmation', 'failed', String(error));
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

export const emailService = new EmailService();